import React from 'react';

import * as Yup from 'yup';

import MyPagePanel from '@/components/ui/box/MyPagePanel';
import Paper from '@/components/ui/box/Paper';
import BackToPageButton from '@/components/ui/buttons/BackToPageButton';
import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import Label from '@/components/ui/forms/Label';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import {
  type ParentNameChangeFormValues,
  HIRAGANA_REGEXP,
  NAME_KANA_MAX_LENGTH,
  NAME_MAX_LENGTH,
  ZENKAKU_REGEXP,
} from '@/interfaces/parent';

import styles from './ParentNameChangeForm.module.scss';

interface Props {
  initialValues: ParentNameChangeFormValues;
  onSubmit: (values: ParentNameChangeFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
  serverError?: string;
}

const validationSchema = Yup.object().shape({
  last_name: Yup.string()
    .trim()
    .required('姓は必須です')
    .matches(ZENKAKU_REGEXP, {
      message: '姓は全角文字で入力してください',
      excludeEmptyString: true,
    })
    .max(NAME_MAX_LENGTH, `姓は${NAME_MAX_LENGTH}文字以内で入力してください`),
  first_name: Yup.string()
    .trim()
    .required('名は必須です')
    .matches(ZENKAKU_REGEXP, {
      message: '名は全角文字で入力してください',
      excludeEmptyString: true,
    })
    .max(NAME_MAX_LENGTH, `名は${NAME_MAX_LENGTH}文字以内で入力してください`),
  last_name_kana: Yup.string()
    .required('姓（かな）は必須です')
    .matches(HIRAGANA_REGEXP, {
      message: '姓（かな）はひらがなで入力してください',
      excludeEmptyString: true,
    })
    .max(
      NAME_KANA_MAX_LENGTH,
      `姓（かな）は${NAME_KANA_MAX_LENGTH}文字以内で入力してください`,
    ),
  first_name_kana: Yup.string()
    .required('名（かな）は必須です')
    .matches(HIRAGANA_REGEXP, {
      message: '名（かな）はひらがなで入力してください',
      excludeEmptyString: true,
    })
    .max(
      NAME_KANA_MAX_LENGTH,
      `名（かな）は${NAME_KANA_MAX_LENGTH}文字以内で入力してください`,
    ),
});

const ParentNameChangeForm: React.FC<Props> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
  serverError,
}) => {
  const {
    registerWithInnerRef,
    handleSubmit,
    formState: { isSubmitting, isValid, errorMessages },
  } = useReactHookForm<ParentNameChangeFormValues>({
    validationSchema,
    defaultValues: initialValues,
  });

  const inputDisabled = isSubmitting || isLoading;
  const buttonDisabled = !isValid || isSubmitting || isLoading;

  const nameErrors = [
    ...(errorMessages.last_name || []),
    ...(errorMessages.first_name || []),
  ];

  const nameKanaErrors = [
    ...(errorMessages.last_name_kana || []),
    ...(errorMessages.first_name_kana || []),
  ];

  const serverErrorMessages = (serverError || '')
    .split(/\r?\n/)
    .filter((msg) => msg.trim() !== '');

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      className={styles.ParentNameChangeForm}
    >
      <Paper>
        <MyPagePanel title="氏名の編集">
          <FormGroup className={styles.FormGroup}>
            <Label>保護者氏名</Label>
            <Field isFullWidth>
              <div className={styles.FieldGroup}>
                <Field isFullWidth>
                  <Input
                    type="text"
                    {...registerWithInnerRef('last_name')}
                    disabled={inputDisabled}
                    placeholder="例）山田"
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    {...registerWithInnerRef('first_name')}
                    disabled={inputDisabled}
                    placeholder="例）太郎"
                  />
                </Field>
              </div>
              <ErrorList errors={nameErrors} />
            </Field>
          </FormGroup>
          <FormGroup className={styles.FormGroup}>
            <Label>保護者氏名（かな）</Label>
            <Field isFullWidth>
              <div className={styles.FieldGroup}>
                <Field isFullWidth>
                  <Input
                    type="text"
                    {...registerWithInnerRef('last_name_kana')}
                    disabled={inputDisabled}
                    placeholder="例）やまだ"
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    {...registerWithInnerRef('first_name_kana')}
                    disabled={inputDisabled}
                    placeholder="例）たろう"
                  />
                </Field>
              </div>
              <ErrorList errors={nameKanaErrors} />
              {serverErrorMessages.length > 0 && (
                <ErrorList errors={serverErrorMessages} />
              )}
            </Field>
          </FormGroup>
        </MyPagePanel>
      </Paper>
      <div className={styles.ButtonArea}>
        <Button
          color="primary"
          type="submit"
          disabled={buttonDisabled}
          className={styles.Button}
        >
          保存
        </Button>
        <BackToPageButton
          onClick={onCancel}
          className={`${styles.Button} ${styles.CancelButton}`}
        >
          キャンセル
        </BackToPageButton>
      </div>
    </form>
  );
};

export default ParentNameChangeForm;
